import React, { useState, useEffect } from 'react';
import { Calendar, Phone, Plus, Target, TrendingUp, Users, Workflow } from 'lucide-react';
import { funnelService } from '../../services/funnelService';
import FunnelVisualization from './components/FunnelVisualization';
import CalendarView from './components/CalendarView';
import CallInfoPanel from './components/CallInfoPanel';
import NotesPanel from './components/NotesPanel';
import WorkflowPanel from './components/WorkflowPanel';
import Icon from '../../components/AppIcon';
import PageLayout from '../../components/ui/PageLayout';
import { useAuth } from '../../contexts/AuthContext';

const SalesFunnelManagement = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('funnel');
  const [funnels, setFunnels] = useState([]);
  const [selectedFunnel, setSelectedFunnel] = useState(null);
  const [leads, setLeads] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user?.id) {
      loadFunnels();
    } else {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (selectedFunnel) {
      loadFunnelData();
    }
  }, [selectedFunnel]);

  const loadFunnels = async () => {
    setLoading(true);
    try {
      const data = await funnelService?.getAllFunnels(user?.id);
      setFunnels(data || []);
      if (data?.length > 0) {
        setSelectedFunnel(data?.[0]);
      }
    } catch (err) {
      setError('Failed to load funnels');
    } finally {
      setLoading(false);
    }
  };

  const loadFunnelData = async () => {
    if (!selectedFunnel?.id) return;
    try {
      // Load leads for this funnel
      const { data: leadsData } = await import('../../lib/supabase')?.then(async ({ supabase }) => {
        return supabase?.from('funnel_leads')?.select('*')?.eq('funnel_id', selectedFunnel?.id)?.limit(50);
      });
      setLeads(leadsData || []);
    } catch (err) {
      console.error('Error loading funnel data:', err);
    }
  };

  const handleCreateFunnel = async () => {
    if (!user?.id) return;
    const newFunnel = {
      name: 'New Sales Funnel',
      description: 'Description for new funnel',
      user_id: user?.id,
      status: 'active'
    };
    try {
      const data = await funnelService?.createFunnel(newFunnel);
      if (data) {
        setFunnels(prev => [data, ...prev]);
        setSelectedFunnel(data);
      }
    } catch (err) {
      setError('Failed to create funnel');
    }
  };

  const tabs = [
    { id: 'funnel', label: 'Funnel Visualization', icon: TrendingUp },
    { id: 'calendar', label: 'Calendar', icon: Calendar },
    { id: 'calls', label: 'Call Info', icon: Phone },
    { id: 'notes', label: 'Notes', icon: Users },
    { id: 'workflow', label: 'Workflow', icon: Workflow }
  ];

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'funnel':
        return (
          <FunnelVisualization
            funnel={selectedFunnel}
            leads={leads}
            analytics={analytics}
            onLeadUpdate={loadFunnelData}
          />
        );
      case 'calendar':
        return <CalendarView userId={user?.id} />;
      case 'calls':
        return <CallInfoPanel leads={leads} onCallLogged={loadFunnelData} />;
      case 'notes':
        return <NotesPanel leads={leads} userId={user?.id} onNoteAdded={loadFunnelData} />;
      case 'workflow':
        return <WorkflowPanel funnel={selectedFunnel} userId={user?.id} />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading funnels...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      {/* Header Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Sales Funnel Management</h1>
            <p className="mt-2 text-muted-foreground">
              Track lead progression, manage pipeline, and optimize conversions
            </p>
          </div>
          <button
            onClick={handleCreateFunnel}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Funnel
          </button>
        </div>
      </div>
      {/* Error Display */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}
      {/* Funnel Selector */}
      {funnels?.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center space-x-4">
            <label className="text-sm font-medium text-foreground">Active Funnel:</label>
            <select
              value={selectedFunnel?.id || ''}
              onChange={(e) => {
                const funnel = funnels?.find(f => f?.id === e?.target?.value);
                setSelectedFunnel(funnel);
              }}
              className="block w-64 pl-3 pr-10 py-2 text-base border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {funnels?.map((funnel) => (
                <option key={funnel?.id} value={funnel?.id}>
                  {funnel?.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
      {/* Analytics Cards */}
      {selectedFunnel && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {[
            { label: 'Total Leads', value: leads?.length || 0, Icon: Target },
            { label: 'Conversion Rate', value: `${analytics?.conversion_rate || 0}%`, Icon: TrendingUp },
            { label: 'Active Leads', value: leads?.filter(l => !l?.is_converted)?.length || 0, Icon: Users },
            { label: 'Converted', value: leads?.filter(l => l?.is_converted)?.length || 0, Icon: TrendingUp }
          ]?.map((card, idx) => (
            <div key={idx} className="bg-card border border-border overflow-hidden shadow-sm rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <card.Icon className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-muted-foreground truncate">{card?.label}</dt>
                      <dd className="text-lg font-medium text-foreground">{card?.value}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Tab Navigation */}
      <div className="mb-6">
        <div className="border-b border-border">
          <nav className="-mb-px flex space-x-8 overflow-x-auto">
            {tabs?.map((tab) => {
              const TabIcon = tab?.icon;
              return (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center space-x-2 py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                    activeTab === tab?.id
                      ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                  }`}
                >
                  <TabIcon className="w-4 h-4" />
                  <span>{tab?.label}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>
      {/* Tab Content */}
      <div className="min-h-[400px]">
        {selectedFunnel ? renderActiveTab() : (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <Target className="w-16 h-16 text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">No Funnels Yet</h3>
            <p className="text-muted-foreground mb-6">Create your first sales funnel to start tracking leads</p>
            <button
              onClick={handleCreateFunnel}
              className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              <Plus className="w-5 h-5 mr-2" />
              Create Your First Funnel
            </button>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default SalesFunnelManagement;